import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, BookOpen, Calendar, Layers } from "lucide-react";

import { useStaffAuth } from "../../context/StaffAuthContext";

import {
  getAllTemplates,
  createTemplate,
  deleteTemplate,
} from "../../api/syllabus/syllabusApi";
import {
  getAllTemplatesPortal,
  createTemplatePortal,
  deleteTemplatePortal,
} from "../../api/syllabus/syllabusPortalApi";

import axios from "axios";
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const axiosInstance = axios.create({ baseURL: API, withCredentials: true });

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isOverdue = (date) => !!date && new Date(date) < new Date();

const emptyForm = {
  title: "",
  subjectId: "",
  classId: "",
  academicYear: "",
  targetCompletionDate: "",
};

const SyllabusTemplateList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();

  const canManage = isPortal ? !!staffAuth?.hasPermission?.("SYLLABUS_FULL") : true;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);

  const [filters, setFilters] = useState({
    classId: "",
    subjectId: "",
    academicYear: "",
    isActive: "true",
  });

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const query = { ...filters };
      if (isPortal) delete query.isActive; // portal API only ever returns active templates
      if (!query.classId) delete query.classId;
      if (!query.subjectId) delete query.subjectId;
      if (!query.academicYear) delete query.academicYear;

      const res = isPortal
        ? await getAllTemplatesPortal(query)
        : await getAllTemplates(query);

      const list = Array.isArray(res) ? res : res.data || res.templates || [];
      setTemplates(list);
    } catch (err) {
      console.error("Failed to load syllabus templates:", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [filters, isPortal]);

  const loadDropdownData = useCallback(async () => {
    try {
      const [subjectsRes, classesRes] = await Promise.all([
        axiosInstance.get(isPortal ? "/subject-portal/all" : "/subjects/all"),
        axiosInstance.get(isPortal ? "/student-portal/classes" : "/classes/all"),
      ]);
      const subjectList = subjectsRes.data?.data || subjectsRes.data?.subjects || subjectsRes.data || [];
      const classList = classesRes.data?.data || classesRes.data?.classes || classesRes.data || [];
      setSubjects(Array.isArray(subjectList) ? subjectList : []);
      setClasses(Array.isArray(classList) ? classList : []);
    } catch (err) {
      console.error("Failed to load subjects/classes:", err);
    }
  }, [isPortal]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (canManage) loadDropdownData();
  }, [canManage, loadDropdownData]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.title.trim() || !form.subjectId || !form.classId || !form.academicYear.trim()) {
      setFormError("Title, subject, class and academic year are required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        subjectId: form.subjectId,
        classId: form.classId,
        academicYear: form.academicYear.trim(),
        targetCompletionDate: form.targetCompletionDate || null,
      };
      const res = isPortal
        ? await createTemplatePortal(payload)
        : await createTemplate(payload);

      const created = res.data || res;
      setForm(emptyForm);
      await loadTemplates();

      const detailPath = isPortal
        ? `/staff-panel/syllabus/template/${created._id}`
        : `/syllabus/template/${created._id}`;
      navigate(detailPath);
    } catch (err) {
      setFormError(err?.response?.data?.message || "Failed to create template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      if (isPortal) await deleteTemplatePortal(id);
      else await deleteTemplate(id);
      setConfirmDeleteId(null);
      await loadTemplates();
    } catch (err) {
      console.error("Failed to delete template:", err);
    }
  };

  const goToDetail = (id) => {
    navigate(isPortal ? `/staff-panel/syllabus/template/${id}` : `/syllabus/template/${id}`);
  };

  const goToProgressEntry = (template) => {
    // Clicking the card jumps to the template detail — teachers pick a section from there
    goToDetail(template._id);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="text-violet-600" size={26} />
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">Syllabus Templates</h1>
      </div>

      <div className={`grid grid-cols-1 ${canManage ? "lg:grid-cols-3" : ""} gap-6`}>
        {/* LEFT — Template list */}
        <div className={canManage ? "lg:col-span-2" : ""}>
          {/* Filter bar */}
          <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
            <select
              value={filters.classId}
              onChange={(e) => handleFilterChange("classId", e.target.value)}
              className="border rounded-xl px-3 py-2 text-sm"
            >
              <option value="">All Classes</option>
              {classes.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.className}
                </option>
              ))}
            </select>

            <select
              value={filters.subjectId}
              onChange={(e) => handleFilterChange("subjectId", e.target.value)}
              className="border rounded-xl px-3 py-2 text-sm"
            >
              <option value="">All Subjects</option>
              {subjects.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Academic Year e.g. 2024-25"
              value={filters.academicYear}
              onChange={(e) => handleFilterChange("academicYear", e.target.value)}
              className="border rounded-xl px-3 py-2 text-sm w-44"
            />

            {!isPortal && (
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange("isActive", e.target.value)}
                className="border rounded-xl px-3 py-2 text-sm"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            )}
          </div>

          {/* List */}
          {loading ? (
            <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="text-center text-neutral-400 dark:text-neutral-500 py-16 bg-white dark:bg-neutral-900 rounded-2xl shadow-sm">
              No syllabus templates found
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => {
                const subject = template.subjectId || {};
                const classInfo = template.classId || {};
                const totalChapters = template.chapters?.length || 0;
                const totalTopics =
                  template.chapters?.reduce((sum, c) => sum + (c.topics?.length || 0), 0) || 0;
                const overdue = isOverdue(template.targetCompletionDate);

                return (
                  <div
                    key={template._id}
                    onClick={() => goToProgressEntry(template)}
                    className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm p-5 cursor-pointer hover:shadow-md transition"
                    style={{ borderLeftColor: subject.color || "#8b5cf6", borderLeftWidth: 4 }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: subject.color || "#8b5cf6" }}
                          />
                          <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">
                            {subject.name || "Subject"}
                          </span>
                          <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 text-xs px-2 py-0.5 rounded-lg">
                            {classInfo.className || "Class"}
                          </span>
                          <span className="text-xs text-neutral-400 dark:text-neutral-500">{template.academicYear}</span>
                        </div>
                        <h3 className="font-bold text-neutral-800 dark:text-neutral-100">{template.title}</h3>

                        <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                          <span className="flex items-center gap-1">
                            <Layers size={14} /> {totalChapters} chapters
                          </span>
                          <span>{totalTopics} topics</span>
                          {template.targetCompletionDate && (
                            <span
                              className={`flex items-center gap-1 ${
                                overdue ? "text-red-500 font-medium" : "text-neutral-400 dark:text-neutral-500"
                              }`}
                            >
                              <Calendar size={14} /> {formatDate(template.targetCompletionDate)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-lg font-medium ${ template.isActive ? "bg-green-100 text-green-700" : "bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400" }`}
                        >
                          {template.isActive ? "Active" : "Inactive"}
                        </span>

                        {canManage && (
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => goToDetail(template._id)}
                              className="p-1.5 rounded-lg hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                            {confirmDeleteId === template._id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDelete(template._id)}
                                  className="text-xs bg-red-500 text-white px-2 py-1 rounded-lg"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  className="text-xs bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 px-2 py-1 rounded-lg"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteId(template._id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-red-400"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT — Create form */}
        {canManage && (
          <div>
            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm p-5 sticky top-6">
              <h2 className="font-bold text-neutral-800 dark:text-neutral-100 mb-4 flex items-center gap-2">
                <Plus size={18} className="text-violet-600" /> New Template
              </h2>

              <form onSubmit={handleCreate} className="space-y-3">
                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => handleFormChange("title", e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
                    placeholder="e.g. Mathematics - Class 10 - 2024-25"
                  />
                </div>

                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">Subject</label>
                  <select
                    value={form.subjectId}
                    onChange={(e) => handleFormChange("subjectId", e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
                  >
                    <option value="">Select subject</option>
                    {subjects.map((s) => (
                      <option key={s._id} value={s._id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">Class</label>
                  <select
                    value={form.classId}
                    onChange={(e) => handleFormChange("classId", e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
                  >
                    <option value="">Select class</option>
                    {classes.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.className}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">Academic Year</label>
                  <input
                    type="text"
                    value={form.academicYear}
                    onChange={(e) => handleFormChange("academicYear", e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
                    placeholder="2024-25"
                  />
                </div>

                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">Target Completion Date</label>
                  <input
                    type="date"
                    value={form.targetCompletionDate}
                    onChange={(e) => handleFormChange("targetCompletionDate", e.target.value)}
                    className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
                  />
                </div>

                {formError && <p className="text-xs text-red-500">{formError}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-violet-600 text-white py-2.5 rounded-xl font-medium hover:bg-violet-700 disabled:opacity-60"
                >
                  {submitting ? "Creating…" : "Create Template"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyllabusTemplateList;